import { memo, useRef, useMemo, useCallback, useEffect, useState, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Crud, CrudMode, notification, rejectionError, transformCrudFormErrors, SelectsOptions, getSelectsOptions } from '@ohpen/enso-ui';
import type { CrudFormComponent, CrudProps, ValidationError } from '@ohpen/enso-ui';
import {
  ChangeRequestOfLenderFirmDraftDto,
  lenderFirmsGetLenderFirm,
  lenderFirmsCreateChangeRequest,
  lendersGetLender,
  FirmGetAllResponse,
  FirmGetById,
  ComplianceStatus,
  CasePermission,
} from '@pmp/api';
import { crudToActionMap, getApiErrorMessage, changeRequestNotificationMessage, changeRequestButtonMessage } from '@pmp/utils';
import { mapLenderFirmFormValuesToDraft } from '../utils';
import { useFeatureToggle } from '@pmp/hooks';
import { CrudFormProps } from '@pmp/types';

import { ValidationErrorRows } from '../interfaces';
import { FirmsFormSchema } from '.';

// Whitespace, '' and undefined all mean "still empty", so typing spaces into a field the server
// called required does not count as resolving it.
const asComparable = (value: unknown): string =>
  JSON.stringify(typeof value === 'string' ? value.trim() || null : (value ?? null));

export const FirmsCrudForm = memo(
  ({ mode, record, valueLists, refreshTable, onClose }: CrudFormProps<FirmGetAllResponse>): ReactElement | null => {
    const ref = useRef<CrudFormComponent>(null);
    const [open, setOpen] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [activeTab, setActiveTab] = useState<string>('registration');
    const [errorRows, setErrorRows] = useState<ValidationErrorRows>({ version: 0, indices: [], messages: [] });
    const { t } = useTranslation();
    const enableFourEyes = useFeatureToggle(['enableFourEyes']);
    const [defaultComplianceStatus, setDefaultComplianceStatus] = useState<ComplianceStatus | undefined>();

    useEffect(() => {
      lendersGetLender()
        .then(({ data }) => {
          setDefaultComplianceStatus(data?.complianceCheckConfiguration?.defaultFirmStatus ?? undefined);
        })
        .catch(() => { });
    }, []);

    useEffect(() => {
      if (mode === CrudMode.EDIT && !record?.id) {
        notification().error({ title: t('global.errors.unexpected') });
        onClose();
        return;
      }
      setOpen(true);
    }, [mode, record, t, onClose]);

    useEffect(() => {
      if (!open || mode !== CrudMode.CREATE) return;
      let frame = 0;
      const seedPermissionDefaults = () => {
        const form = ref.current?.form;
        if (!form) {
          frame = requestAnimationFrame(seedPermissionDefaults);
          return;
        }
        for (const field of ['btl', 'rbtl', 'rmc'] as const) {
          if (form.getFieldValue(['registrationDetails', field]) == null) {
            form.setFieldValue(['registrationDetails', field], CasePermission.CREATE_AND_MANAGE);
          }
        }
      };
      seedPermissionDefaults();
      return () => cancelAnimationFrame(frame);
    }, [open, mode]);

    useEffect(() => {
      if (!open || mode === CrudMode.CREATE || !record?.id) return;
      setFetching(true);
      lenderFirmsGetLenderFirm({ path: { firmId: record.id } })
        .then(({ data }) => {
          ref.current?.form?.setFieldsValue({
            ...data,
            tradingNames:
              data?.tradingNames?.map(tn => ({
                ...tn,
                isActive: (tn as Record<string, unknown>).isActive ?? true,
                bdm: {
                  regionId: ((tn as Record<string, unknown>).regionId as string | null | undefined) ?? null,
                  regionName: ((tn as Record<string, unknown>).region as string | null | undefined) ?? null,
                  bdmId: ((tn as Record<string, unknown>).bdmId as string | null | undefined) ?? null,
                  bdmName: ((tn as Record<string, unknown>).businessDevelopmentManager as string | null | undefined) ?? null,
                },
              })) ?? [],
          });
        })
        .catch(error => {
          notification().error({ title: getApiErrorMessage(error) });
        })
        .finally(() => setFetching(false));
    }, [open, mode, record?.id, t]);

    const setDisableSave = useCallback((value: boolean) => {
      ref.current?.setDisableSave?.(value);
    }, []);

    // Server-side errors arrive through setFields and the fields carry no client-side rules, so
    // antd never revalidates them. Each one is kept with the value it was reported for and cleared
    // as soon as that field changes, instead of lingering until the next Save.
    const serverErrorsRef = useRef<Array<{ name: (string | number)[]; value: unknown }>>([]);

    const clearResolvedServerErrors = useCallback(() => {
      const form = ref.current?.form;
      if (!form || !serverErrorsRef.current.length) return;

      const resolved: ValidationError[] = [];
      const unresolved: Array<{ name: (string | number)[]; value: unknown }> = [];
      for (const reported of serverErrorsRef.current) {
        if (asComparable(form.getFieldValue(reported.name)) === asComparable(reported.value)) {
          unresolved.push(reported);
        } else {
          resolved.push({ name: reported.name, errors: [] });
        }
      }

      if (!resolved.length) return;
      serverErrorsRef.current = unresolved;
      form.setFields(resolved);
    }, []);

    const mapFormValuesToDraft = useCallback((values: FirmGetById) => mapLenderFirmFormValuesToDraft(values, record), [record]);

    const onConfirm: (formData: FirmGetById) => Promise<ValidationError[] | void> = useCallback(
      (values: FirmGetById) => {
        if (!mode || !crudToActionMap[mode]) return Promise.reject(rejectionError('mode is required'));

        const data = mapFormValuesToDraft(values);
        const body: ChangeRequestOfLenderFirmDraftDto = {
          data,
          actionType: crudToActionMap[mode],
        };

        return lenderFirmsCreateChangeRequest({ body }).then(
          ({ data: responseData }: { data: { isValid?: boolean; errors?: Record<string, unknown> } }) => {
            if (!responseData.isValid) {
              const form = ref.current?.form;
              if (form) {
                const errorFields = form.getFieldsError().map(f => ({ name: f.name, errors: [] }));
                if (errorFields.length) form.setFields(errorFields);
              }

              const errors = { ...responseData.errors } as Record<string, unknown>;
              const byIndex = errors.tradingAddressesByIndex as Record<string, Record<string, string[]>> | undefined;
              if (byIndex) {
                const tradingNames: Record<string, Record<string, string[]>> = {};
                for (const [idx, fields] of Object.entries(byIndex)) {
                  tradingNames[idx] = {};
                  for (const [field, msgs] of Object.entries(fields)) {
                    const camelField = field.charAt(0).toLowerCase() + field.slice(1);
                    tradingNames[idx][camelField] = msgs;
                  }
                }
                (errors as Record<string, unknown>).tradingNames = tradingNames;
                delete errors.tradingAddressesByIndex;
              }

              const payments = errors.payments as Record<string, unknown> | undefined;
              const bankAccountsByOperation = payments?.bankAccountsByOperation as Record<string, string[]> | undefined;
              if (payments && bankAccountsByOperation) {
                const operationErrors: Record<string, Record<string, string[]>> = {};
                for (const [operation, msgs] of Object.entries(bankAccountsByOperation)) {
                  operationErrors[operation] = { accountName: msgs };
                }
                payments.operations = operationErrors;
                delete payments.bankAccountsByOperation;
              }

              const errorIndices = byIndex ? Object.keys(byIndex).map(Number) : [];
              const tradingAddressMessages = Array.isArray(errors.tradingAddresses)
                ? (errors.tradingAddresses as string[]).filter(Boolean)
                : [];
              setErrorRows(prev => ({ version: prev.version + 1, indices: errorIndices, messages: tradingAddressMessages }));

              const transformed = transformCrudFormErrors(errors as Record<string, string[]>);
              serverErrorsRef.current = transformed.map(f => ({ name: f.name, value: form?.getFieldValue(f.name) }));
              // Collapsed address rows unmount their fields; re-apply errors after
              // the error-driven expansion has mounted them, or they render nowhere.
              requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                  ref.current?.form?.setFields(transformed);
                });
              });

              return transformed;
            }
            serverErrorsRef.current = [];
            setErrorRows({ version: 0, indices: [], messages: [] });
            if (mode === CrudMode.EDIT || !enableFourEyes) refreshTable();
            notification().success({
              title: changeRequestNotificationMessage(t, enableFourEyes),
            });
          },
        );
      },
      [mode, refreshTable, t, enableFourEyes, mapFormValuesToDraft],
    );

    const handleOnClose = useCallback(() => {
      setOpen(false);
      setErrorRows({ version: 0, indices: [], messages: [] });
      setTimeout(() => {
        onClose();
      }, 300);
    }, [onClose]);

    const title = useMemo(() => {
      if (!mode) return '';
      const titles: { [key: string]: string } = {
        [CrudMode.VIEW]: t('global.labels.viewX', { x: t('lender.firms.labels.firm') }),
        [CrudMode.CREATE]: t('global.labels.createX', { x: t('lender.firms.labels.firm') }),
        [CrudMode.EDIT]: t('global.labels.editX', { x: t('lender.firms.labels.firm') }),
      };
      return titles[mode];
    }, [mode, t]);

    const selectsOptions: SelectsOptions = useMemo(() => (valueLists && getSelectsOptions(valueLists)) || {}, [valueLists]);

    const crudProps: CrudProps<FirmGetById> = useMemo(() => {
      return {
        ref,
        open,
        title,
        labels: {
          confirm: mode === CrudMode.EDIT ? t('global.labels.save') : changeRequestButtonMessage(t, enableFourEyes),
        },
        mode,
        customForm: true,
        autoFocusFirstInput: mode !== CrudMode.VIEW,
        width: 1100,
        wrapperCol: { flex: 'auto' },
        disableDrawerBodyScroll: true,
        hideConfirm: activeTab !== 'registration',
        onClose: handleOnClose,
        onConfirm,
        onValuesChange: clearResolvedServerErrors,
      };
    }, [open, title, mode, t, handleOnClose, onConfirm, enableFourEyes, activeTab, clearResolvedServerErrors]);

    return (
      <Crud loading={fetching} {...crudProps}>
        <FirmsFormSchema form={ref.current?.form} mode={mode ?? CrudMode.VIEW} selectsOptions={selectsOptions} defaultComplianceStatus={defaultComplianceStatus} entityId={record?.id} onTabChange={setActiveTab} errorRows={errorRows} setDisableSave={setDisableSave} />
      </Crud>
    );
  },
);
